/**
 * strategyExplainer.js
 *
 * Generates structured, trader-friendly explanations for schema-v2 strategy
 * sections from the raw condition tree.
 *
 * Design:
 * - Phrase-based i18n with EN/HI keys and automatic EN fallback
 * - Language-aware price condition templates (grammar only in Hindi)
 * - Technical terms (EMA, RSI, Supertrend, etc.) always stay in English
 * - generateSectionExplanation() is the single public entry point
 *
 * Adding new languages: add a new key block to PHRASES — nothing else changes.
 * Adding new sections (short_entry, exits): pass sectionKey — already handled.
 */

// ─── Phrase map ─────────────────────────────────────────────────────────────

const PHRASES = {
  en: {
    'section.long_entry':  'Long entry',
    'section.short_entry': 'Short entry',
    'section.long_exit':   'Long exit',
    'section.short_exit':  'Short exit',

    'intro.immediate':    '{section} will be taken immediately when:',
    'intro.next_candle':  '{section} will be taken on the next candle close, if:',
    'intro.ltp_cross':    '{section} will trigger on LTP cross, if:',
    'intro.cross_close':  '{section} will trigger on cross + close, if:',
    'intro.default':      '{section} will be triggered when:',

    'connector.and':      'AND',
    'connector.or':       'OR',
    'connector.or.group': 'At least ONE of the following:',

    'lifecycle.replace':    "New setup always gets priority if the earlier one hasn't triggered.",
    'lifecycle.keep_first': 'First active setup is held until it triggers or expires.',
    'lifecycle.expires_n':  'Setup expires after {n} candles if not triggered.',

    'empty': 'No conditions to explain yet.',
  },
  hi: {
    'section.long_entry':  'लॉन्ग एंट्री',
    'section.short_entry': 'शॉर्ट एंट्री',
    'section.long_exit':   'लॉन्ग एग्ज़िट',
    'section.short_exit':  'शॉर्ट एग्ज़िट',

    'intro.immediate':    '{section} तुरंत ली जाएगी जब:',
    'intro.next_candle':  'अगली कैंडल बंद होने पर {section} ली जाएगी, यदि:',
    'intro.ltp_cross':    'LTP क्रॉस होने पर {section} ट्रिगर होगी, यदि:',
    'intro.cross_close':  'क्रॉस और क्लोज़ होने पर {section} ट्रिगर होगी, यदि:',
    'intro.default':      '{section} तब ट्रिगर होगी जब:',

    'connector.and':      'और',
    'connector.or':       'या',
    'connector.or.group': 'निम्नलिखित में से कम से कम एक शर्त पूरी हो:',

    'lifecycle.replace':    'अगर पहला सेटअप ट्रिगर नहीं हुआ, तो नई सेटअप कैंडल को प्राथमिकता मिलेगी।',
    'lifecycle.keep_first': 'पहला एक्टिव सेटअप तब तक बना रहेगा जब तक ट्रिगर या एक्सपायर न हो।',
    'lifecycle.expires_n':  '{n} कैंडल में ट्रिगर न होने पर सेटअप एक्सपायर होगा।',

    'empty': 'अभी कोई शर्त नहीं है।',
  },
};

/**
 * t(key, lang, vars) — phrase lookup with EN fallback.
 * vars: { section: 'Long entry' } → replaces {section} in template.
 */
function t(key, lang, vars = {}) {
  const str = PHRASES[lang]?.[key] ?? PHRASES.en[key] ?? key;
  return Object.entries(vars).reduce((s, [k, v]) => s.replace(`{${k}}`, v), str);
}

// ─── Candle field display names & verb mapping ───────────────────────────────

const PRICE_DISPLAY = {
  ltp:                  'LTP',
  open:                 'Open',
  high:                 'High',
  low:                  'Low',
  close:                'Close',
  volume:               'Volume',
  range:                'Range',
  body:                 'Body',
  upper_wick:           'Upper Wick',
  lower_wick:           'Lower Wick',
  body_pct_range:       'Body % of Range',
  upper_wick_pct_range: 'UW % of Range',
  lower_wick_pct_range: 'LW % of Range',
};

// Verb per field — null means LTP pattern (no verb, uses comparator directly)
const PRICE_VERB = {
  close:                'closes',
  ltp:                  null,
  open:                 'opens',
  high:                 null,
  low:                  null,
  volume:               'is',
  range:                'is',
  body:                 'is',
  upper_wick:           'is',
  lower_wick:           'is',
  body_pct_range:       'is',
  upper_wick_pct_range: 'is',
  lower_wick_pct_range: 'is',
};

// ─── Indicator formatter ─────────────────────────────────────────────────────

/**
 * Format an indicator operand to a compact readable string.
 * Supertrend: detects bullish/bearish direction from the comparator
 * to emit "Green Supertrend(10,3) 🟢" or "Red Supertrend(10,3) 🔴".
 */
function formatIndicatorText(op, comparator) {
  if (!op) return '?';
  const params = op.params ? Object.values(op.params).join(',') : '';
  const out    = op.output ? `.${op.output}` : '';

  if (op.name === 'SUPERTREND') {
    const isBullish = ['GT', 'GTE', 'CROSS_ABOVE'].includes(comparator);
    const colorWord = isBullish ? 'Green' : 'Red';
    const emoji     = isBullish ? '🟢'    : '🔴';
    return params ? `${colorWord} Supertrend(${params}) ${emoji}` : `${colorWord} Supertrend ${emoji}`;
  }

  return params ? `${op.name}(${params})${out}` : `${op.name}${out}`;
}

// ─── Right-operand formatter (language-agnostic) ─────────────────────────────

/**
 * Format the right side of a condition.
 * Technical values (indicator names, numbers) stay in English for all languages.
 */
function formatRight(op, comparator) {
  if (!op) return '?';
  if (op.kind === 'constant') return String(op.value ?? '?');
  if (op.source === 'indicator') return formatIndicatorText(op, comparator);
  if (op.source === 'price') {
    const offset = parseInt(op.offset ?? 0, 10);
    const pfx = offset === 1 ? 'prev ' : offset > 1 ? `(\u2212${offset}) ` : '';
    return `${pfx}${PRICE_DISPLAY[op.name] || op.name.toUpperCase()}`;
  }
  return '?';
}

// ─── Price condition templates ───────────────────────────────────────────────

const EN_COMP = {
  GT:          'ABOVE',
  LT:          'BELOW',
  GTE:         'ABOVE OR EQUAL TO',
  LTE:         'BELOW OR EQUAL TO',
  EQ:          'EQUALS',
  CROSS_ABOVE: 'CROSSES ABOVE',
  CROSS_BELOW: 'CROSSES BELOW',
};

function fmtPriceEn(priceName, comparator, rightText, offset) {
  const pfx   = offset === 1 ? 'One candle back \u2014' : offset > 1 ? `${offset} candles back \u2014` : 'Candle';
  const comp  = EN_COMP[comparator] || comparator;
  const field = PRICE_DISPLAY[priceName] || priceName.toUpperCase();
  const verb  = PRICE_VERB[priceName];

  if (verb === null || verb === undefined) {
    // High, Low, LTP: field identifier + comparator, no verb
    return `${pfx} ${field} ${comp} ${rightText}`;
  }
  // Action verbs that imply the field (e.g. "closes" for Close, "opens" for Open):
  // omit the redundant field label to avoid "Close closes ABOVE ..." output.
  if (verb === 'closes' || verb === 'opens') {
    return `${pfx} ${verb} ${comp} ${rightText}`;
  }
  // Stative verbs (e.g. "is" for Body, Volume, etc.): field + verb + comparator
  return `${pfx} ${field} ${verb} ${comp} ${rightText}`;
}

function fmtPriceHi(priceName, comparator, rightText, offset) {
  const pfx = offset === 1 ? '1 \u0915\u0948\u0902\u0921\u0932 \u092a\u0939\u0932\u0947 \u2014' : offset > 1 ? `${offset} \u0915\u0948\u0902\u0921\u0932 \u092a\u0939\u0932\u0947 \u2014` : '\u0915\u0948\u0902\u0921\u0932';

  if (priceName === 'close') {
    if (['GT', 'GTE'].includes(comparator))  return `${pfx} ${rightText} के ऊपर बंद हो`;
    if (['LT', 'LTE'].includes(comparator))  return `${pfx} ${rightText} के नीचे बंद हो`;
    if (comparator === 'CROSS_ABOVE')         return `${pfx} ${rightText} को ऊपर क्रॉस करे`;
    if (comparator === 'CROSS_BELOW')         return `${pfx} ${rightText} को नीचे क्रॉस करे`;
    if (comparator === 'EQ')                  return `${pfx} ${rightText} के बराबर बंद हो`;
  }

  // Fallback to English for non-close price fields or unmapped comparators
  return fmtPriceEn(priceName, comparator, rightText, offset);
}

// ─── Condition-to-text ───────────────────────────────────────────────────────

const INDICATOR_COMP_SYMBOLS = {
  GT: '>', LT: '<', GTE: '≥', LTE: '≤', EQ: '=',
  CROSS_ABOVE: 'CROSSES ABOVE', CROSS_BELOW: 'CROSSES BELOW',
};

function conditionToText(node, lang) {
  if (!node || node.node_type !== 'condition') return '';
  const { left, comparator, right } = node;
  const rightText = formatRight(right, comparator);
  const offset    = parseInt(left?.offset ?? 0, 10);

  // Price left-operand → language-specific phrasing
  if (left?.kind === 'series' && left?.source === 'price') {
    if (lang === 'hi') return fmtPriceHi(left.name, comparator, rightText, offset);
    return fmtPriceEn(left.name, comparator, rightText, offset);
  }

  // Indicator or constant left-operand → keep in English for both languages
  const offPfx  = offset === 1 ? 'prev ' : offset > 1 ? `(\u2212${offset}) ` : '';
  const leftText = left?.source === 'indicator'
    ? `${offPfx}${formatIndicatorText(left, comparator)}`
    : String(left?.value ?? '?');
  const sym = INDICATOR_COMP_SYMBOLS[comparator] || comparator;
  return `${leftText} ${sym} ${rightText}`;
}

// ─── Node-to-inline text (for OR sub-items that may themselves be groups) ────

function nodeToInlineText(node, lang) {
  if (!node) return '';
  if (node.node_type === 'condition') return conditionToText(node, lang);
  if (node.node_type === 'group') {
    const children = node.children || [];
    const sep = node.operator === 'OR'
      ? ` ${t('connector.or', lang)} `
      : ` ${t('connector.and', lang)} `;
    return children.map(c => nodeToInlineText(c, lang)).filter(Boolean).join(sep);
  }
  return '';
}

// ─── Tree → ExplanationItem[] ────────────────────────────────────────────────

/**
 * ExplanationItem types:
 *   { type: 'condition', text: string }
 *   { type: 'and',       text: string }
 *   { type: 'or_group',  label: string, items: { type:'condition', text:string }[] }
 */
export function treeToExplanationItems(node, lang) {
  if (!node) return [];

  // Single condition at root
  if (node.node_type === 'condition') {
    return [{ type: 'condition', text: conditionToText(node, lang) }];
  }

  if (node.node_type !== 'group') return [];
  const children = node.children || [];
  if (children.length === 0) return [];

  // Root is OR → single "at least one" block
  if (node.operator === 'OR') {
    return [{
      type:  'or_group',
      label: t('connector.or.group', lang),
      items: children.map(c => ({ type: 'condition', text: nodeToInlineText(c, lang) })),
    }];
  }

  // Root is AND → bullet per child, AND connectors between them
  const items = [];
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.node_type === 'condition') {
      items.push({ type: 'condition', text: conditionToText(child, lang) });
    } else if (child.node_type === 'group') {
      if (child.operator === 'OR') {
        items.push({
          type:  'or_group',
          label: t('connector.or.group', lang),
          items: (child.children || []).map(c => ({
            type: 'condition',
            text: nodeToInlineText(c, lang),
          })),
        });
      } else {
        // AND sub-group — render inline
        items.push({ type: 'condition', text: nodeToInlineText(child, lang) });
      }
    }
    if (i < children.length - 1) {
      items.push({ type: 'and', text: t('connector.and', lang) });
    }
  }
  return items;
}

// ─── Public API ──────────────────────────────────────────────────────────────

const EXEC_MODE_TO_INTRO_KEY = {
  IMMEDIATE:         'intro.immediate',
  NEXT_CANDLE_CLOSE: 'intro.next_candle',
  LTP_CROSS:         'intro.ltp_cross',
  CROSS_AND_CLOSE:   'intro.cross_close',
};

/**
 * generateSectionExplanation({ sectionKey, tree, execution, lifecycle, lang })
 *
 * @param {string} sectionKey  - 'long_entry' | 'short_entry' | 'long_exit' | 'short_exit'
 * @param {object} tree        - Root condition tree node (with _id stripped or not — both fine)
 * @param {object} execution   - Section execution config { mode, ... }
 * @param {object} lifecycle   - Section lifecycle config { validity, expiry_candles, replacement }
 * @param {string} lang        - 'en' | 'hi' (defaults to 'en')
 *
 * @returns {{ intro: string, items: ExplanationItem[] } | null}
 *   null when tree is empty or has no conditions.
 *
 * Lifecycle behavior is appended as a final bullet (after a spacer) so the
 * explanation box is self-contained and no separate note card is needed.
 */
export function generateSectionExplanation({ sectionKey, tree, execution = {}, lifecycle = {}, lang = 'en' }) {
  if (!tree) return null;
  const hasChildren = tree.node_type === 'group'
    ? (tree.children || []).length > 0
    : tree.node_type === 'condition';
  if (!hasChildren) return null;

  const section  = t(`section.${sectionKey}`, lang);
  const introKey = EXEC_MODE_TO_INTRO_KEY[execution.mode] ?? 'intro.default';
  const items    = treeToExplanationItems(tree, lang);

  // Append lifecycle behavior as a visually-separated final bullet
  items.push({ type: 'spacer' });
  const n = Math.max(1, parseInt(lifecycle.expiry_candles, 10) || 3);
  if (lifecycle.validity === 'EXPIRE_AFTER_N_CANDLES') {
    items.push({ type: 'condition', text: t('lifecycle.expires_n', lang, { n }) });
  }
  const replKey = lifecycle.replacement === 'KEEP_FIRST' ? 'lifecycle.keep_first' : 'lifecycle.replace';
  items.push({ type: 'condition', text: t(replKey, lang) });

  return {
    intro: t(introKey, lang, { section }),
    items,
  };
}
